/**
 * Curated parameter metadata sidecar — the 80-ish ArduCopter params an
 * operator is most likely to want to read or write during setup, lifted
 * from `apm.pdef.xml` (the canonical ArduPilot parameter definitions).
 *
 * This is intentionally a SUBSET, not the full ~1368-entry table. Hard
 * tradeoff: shipping the whole XML embedded is ~5 MB compressed and
 * couples every Console build to a specific ArduPilot release. A small
 * curated sidecar covers the high-frequency setup params and clearly
 * tells the operator "no docs in app" for everything else — they can
 * still write the param, they just won't see the description here.
 *
 * Add entries as they become useful. Source of truth on extending:
 * https://github.com/ArduPilot/ardupilot/blob/master/Tools/autotest/param_metadata
 */

export interface ParamMeta {
  /** Param name as it appears on the autopilot, e.g. "FRAME_CLASS". */
  id: string;
  /** Long-form description. */
  description: string;
  /** Unit ("m", "deg", "%", "Hz", "V"); empty for unitless. */
  units?: string;
  /** Minimum / maximum sanity range (inclusive). Hub doesn't enforce —
   *  the dialog warns if the operator's value falls outside. */
  min?: number;
  max?: number;
  /** Recommended increment for the editor's spinner. */
  increment?: number;
  /** When true, the change only takes effect after the autopilot reboots.
   *  The dialog surfaces this prominently and offers a Reboot button. */
  rebootRequired?: boolean;
  /** Operator categorisation that overrides the prefix-based bucket. */
  category?: string;
  /** Enumerated values for params whose semantics are discrete
   *  (FRAME_CLASS = 1 means "Quad" etc.). Order matters for picker UIs. */
  values?: { value: number; label: string }[];
}

export const PARAM_META: ParamMeta[] = [
  {
    id: 'FRAME_CLASS',
    description:
      'Vehicle frame class. Changes how the autopilot interprets motor channels and which mixer it loads. Requires reboot.',
    rebootRequired: true,
    values: [
      { value: 0, label: 'Undefined' },
      { value: 1, label: 'Quad' },
      { value: 2, label: 'Hexa' },
      { value: 3, label: 'Octa' },
      { value: 4, label: 'OctaQuad' },
      { value: 5, label: 'Y6' },
      { value: 6, label: 'Heli' },
      { value: 7, label: 'Tri' },
      { value: 10, label: 'Single/Coax Heli' },
      { value: 12, label: 'DodecaHexa' },
      { value: 14, label: 'Deca' },
    ],
  },
  {
    id: 'FRAME_TYPE',
    description:
      'Motor layout within the frame class. For a quad: 0=Plus, 1=X, 2=V, 3=H, etc. Must match the physical airframe. Requires reboot.',
    rebootRequired: true,
    values: [
      { value: 0, label: 'Plus (+)' },
      { value: 1, label: 'X' },
      { value: 2, label: 'V' },
      { value: 3, label: 'H' },
      { value: 4, label: 'V-Tail' },
      { value: 5, label: 'A-Tail' },
      { value: 10, label: 'Y6B' },
    ],
  },
  {
    id: 'ARMING_CHECK',
    description:
      'Bitfield of pre-arm checks to run. Set to 1 to enable ALL (recommended). Disabling individual checks is fine for bench testing but dangerous in the field. Each bit corresponds to a sensor or condition: 2=barometer, 4=compass, 8=GPS, 16=INS, 32=params, 64=RC, etc.',
    min: 0,
    max: 1048575,
    rebootRequired: false,
    category: 'ARMING',
  },
  {
    id: 'ARMING_REQUIRE',
    description: 'Require arming via GCS / TX before motors will spin. 0=Disabled (DANGEROUS), 1=Enabled (default).',
    values: [
      { value: 0, label: 'Disabled (DANGEROUS)' },
      { value: 1, label: 'Required' },
    ],
  },
  {
    id: 'BATT_MONITOR',
    description:
      'How to monitor the main battery. 0=Disabled, 3=Analog Voltage only, 4=Voltage + Current (use a current sensor), 6=ESC telemetry, 8=DroneCAN, 15=ESC + sum.',
    rebootRequired: true,
    values: [
      { value: 0, label: 'Disabled' },
      { value: 3, label: 'Voltage only' },
      { value: 4, label: 'Voltage + Current' },
      { value: 6, label: 'ESC telemetry' },
      { value: 8, label: 'DroneCAN BMS' },
    ],
  },
  {
    id: 'BATT_CAPACITY',
    description: 'Battery capacity in mAh. Used by the % remaining estimator and failsafe trigger.',
    units: 'mAh',
    min: 0,
    max: 100000,
    increment: 100,
  },
  {
    id: 'BATT_LOW_VOLT',
    description: 'Voltage below which the LOW battery failsafe triggers. Set per cell × number of cells, leaving headroom.',
    units: 'V',
    min: 0,
    max: 60,
    increment: 0.1,
  },
  {
    id: 'BATT_CRT_VOLT',
    description: 'Voltage below which the CRITICAL battery failsafe triggers. Should be below BATT_LOW_VOLT.',
    units: 'V',
    min: 0,
    max: 60,
    increment: 0.1,
  },
  {
    id: 'BATT_FS_LOW_ACT',
    description: 'Action on LOW battery failsafe.',
    values: [
      { value: 0, label: 'None' },
      { value: 1, label: 'Land' },
      { value: 2, label: 'RTL' },
      { value: 3, label: 'SmartRTL or RTL' },
      { value: 4, label: 'SmartRTL or Land' },
      { value: 5, label: 'Terminate' },
    ],
  },
  {
    id: 'FS_THR_ENABLE',
    description: 'RC failsafe behaviour when the receiver signal is lost.',
    values: [
      { value: 0, label: 'Disabled' },
      { value: 1, label: 'Always RTL' },
      { value: 2, label: 'Continue if Auto, RTL otherwise' },
      { value: 3, label: 'Always Land' },
      { value: 4, label: 'SmartRTL or RTL' },
      { value: 5, label: 'SmartRTL or Land' },
    ],
  },
  {
    id: 'COMPASS_USE',
    description: 'Use the first compass. Set to 0 only if compass is faulty and you accept worse heading.',
    values: [
      { value: 0, label: 'Disabled' },
      { value: 1, label: 'Enabled' },
    ],
  },
  {
    id: 'COMPASS_DEC',
    description: 'Magnetic declination in radians. Auto-set if COMPASS_AUTODEC=1; only override for special venues.',
    units: 'rad',
    min: -1,
    max: 1,
    increment: 0.001,
  },
  {
    id: 'COMPASS_AUTODEC',
    description: 'Auto-set magnetic declination from GPS position once a 3D fix is available.',
    values: [
      { value: 0, label: 'Disabled' },
      { value: 1, label: 'Enabled' },
    ],
  },
  {
    id: 'GPS_TYPE',
    description: 'Primary GPS protocol. 1=AUTO is right for most setups; explicit values pin a single protocol for noisy environments.',
    values: [
      { value: 0, label: 'None' },
      { value: 1, label: 'AUTO' },
      { value: 2, label: 'uBlox' },
      { value: 5, label: 'NMEA' },
      { value: 9, label: 'DroneCAN' },
    ],
  },
  {
    id: 'GPS_HDOP_GOOD',
    description: 'Maximum HDOP value considered "good" for arming. Lower is stricter. Default 1.4.',
    units: '',
    min: 1.0,
    max: 5.0,
    increment: 0.1,
  },
  {
    id: 'MOT_THST_HOVER',
    description:
      'Auto-estimated hover throttle. Set ~0.5 initially for X-class quads; AUTO_HOVER will refine. Wildly wrong values cause altitude instability.',
    min: 0.125,
    max: 0.75,
    increment: 0.01,
  },
  {
    id: 'MOT_THST_EXPO',
    description: 'Motor thrust exponent (motor curve linearisation). 0.65 default for most ESCs; 0 for pure linear (rare).',
    min: -1,
    max: 1,
    increment: 0.05,
  },
  {
    id: 'MOT_PWM_MIN',
    description: 'Minimum PWM the autopilot sends to ESCs at zero throttle. Must match your ESC calibration.',
    units: 'µs',
    min: 800,
    max: 2000,
    rebootRequired: true,
  },
  {
    id: 'MOT_PWM_MAX',
    description: 'Maximum PWM the autopilot sends to ESCs at full throttle.',
    units: 'µs',
    min: 800,
    max: 2200,
    rebootRequired: true,
  },
  {
    id: 'WPNAV_SPEED',
    description: 'Horizontal speed during auto missions and Goto, in cm/s. ArduCopter unit is cm/s — careful, 500 = 5 m/s.',
    units: 'cm/s',
    min: 20,
    max: 2000,
    increment: 10,
  },
  {
    id: 'WPNAV_SPEED_UP',
    description: 'Maximum climb rate in Auto / Goto, cm/s.',
    units: 'cm/s',
    min: 10,
    max: 1000,
    increment: 10,
  },
  {
    id: 'WPNAV_SPEED_DN',
    description: 'Maximum descent rate in Auto / Goto, cm/s. Conservative values are kinder to props in tight spaces.',
    units: 'cm/s',
    min: 10,
    max: 500,
    increment: 10,
  },
  {
    id: 'WPNAV_RADIUS',
    description: 'Waypoint acceptance radius, cm.',
    units: 'cm',
    min: 10,
    max: 1000,
    increment: 10,
  },
  {
    id: 'RTL_ALT',
    description: 'Altitude (cm above home) to climb to before returning when RTL fires. 1500 = 15 m is a common safe default.',
    units: 'cm',
    min: 0,
    max: 8000,
    increment: 100,
  },
  {
    id: 'RTL_SPEED',
    description: 'Horizontal speed during RTL, cm/s. 0 = use WPNAV_SPEED.',
    units: 'cm/s',
    min: 0,
    max: 2000,
    increment: 10,
  },
  {
    id: 'FENCE_ENABLE',
    description: 'Master geofence enable. Sub-fences (alt / radius / polygon) are configured separately.',
    values: [
      { value: 0, label: 'Disabled' },
      { value: 1, label: 'Enabled' },
    ],
  },
  {
    id: 'FENCE_ALT_MAX',
    description: 'Maximum altitude before the fence triggers. 0 = no max-alt fence.',
    units: 'm',
    min: 0,
    max: 1000,
    increment: 1,
  },
  {
    id: 'FENCE_RADIUS',
    description: 'Cylindrical fence radius around home. 0 = no radius fence.',
    units: 'm',
    min: 0,
    max: 10000,
    increment: 10,
  },
  {
    id: 'LAND_SPEED',
    description: 'Descent speed during the final phase of LAND, cm/s. Slower = gentler touchdown.',
    units: 'cm/s',
    min: 20,
    max: 200,
    increment: 5,
  },
  {
    id: 'EK3_ENABLE',
    description: 'Enable the EKF3 estimator (recommended; EKF2 is deprecated).',
    values: [
      { value: 0, label: 'Disabled' },
      { value: 1, label: 'Enabled' },
    ],
    rebootRequired: true,
  },
  {
    id: 'INS_GYR_CAL',
    description: 'Whether to recalibrate gyros on each boot. 1 = always (recommended).',
    values: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Start-up' },
      { value: 2, label: 'Auto' },
    ],
  },
  {
    id: 'SR0_EXTRA1',
    description:
      'Stream rate for the EXTRA1 group on serial 0 (ATTITUDE / AHRS). Higher = smoother attitude in GCS but more bandwidth. Hz.',
    units: 'Hz',
    min: 0,
    max: 50,
    increment: 1,
  },
  {
    id: 'SR0_POSITION',
    description: 'Stream rate for the POSITION group on serial 0 (GLOBAL_POSITION_INT). Hz.',
    units: 'Hz',
    min: 0,
    max: 20,
    increment: 1,
  },
];

const BY_ID = new Map(PARAM_META.map((m) => [m.id, m]));

export function paramMeta(id: string): ParamMeta | undefined {
  return BY_ID.get(id);
}
